import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Params, Router, NavigationExtras } from '@angular/router';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
/**
 * Dieser technische Service schreibt Parameter in die URL und liest sie aus.
 * Darüber hinaus beinhaltet er Funktionalität fürs programmatische Routing.
 */
export class UrlService {
  private readonly router = inject(Router);
  private readonly activeRoute = inject(ActivatedRoute);

  public static VALUE_SEPARATOR = ',';
  public static AKTUELLE_SEITE = 'seite';
  public static SELECTED_ANGEBOT = 'angebot';
  public static DISABLE_CORRECTION = 'dac';

  private queue = Promise.resolve(true);

  public getQueryParameterValue(key: string): Observable<string | null> {
    return this.activeRoute.queryParamMap.pipe(
      map((params) => params.get(key)),
      distinctUntilChanged()
    );
  }

  public getMultipleQueryParameterValues(
    keys: Array<string>
  ): Observable<Map<string, string | null>> {
    return this.activeRoute.queryParamMap.pipe(
      map((params) => {
        const queriedParams = new Map();
        for (const key of keys) {
          queriedParams.set(key, params.get(key));
        }
        return queriedParams;
      }),
      distinctUntilChanged((previous, current) => {
        if (previous?.size === current?.size) {
          for (const [prevKey, prevValue] of previous) {
            if (!(current.has(prevKey) && current.get(prevKey) === prevValue)) {
              return false;
            }
          }
          return true;
        }
        return false;
      })
    );
  }

  public getQueryParameterValues(key: string): Observable<string[]> {
    return this.getQueryParameterValue(key).pipe(
      map((values) => values?.split(UrlService.VALUE_SEPARATOR) ?? [])
    );
  }

  /**
   * Angular bietet die URL nur in komplexen Datentypen an.
   * Manchmal ist aber der String sinnvoll oder nötig, der hier wieder zusammengebaut wird.
   * So können z.B. URLs auf Links hinterlegt werden fürs Öffnen in einem neuen Tab.
   * @returns Url Query Parameter String, e.g. "?seite=1"
   */
  public getQueryParameterString(): Observable<string> {
    return this.activeRoute.queryParamMap.pipe(
      map((params) => {
        return '?' + params.keys.map((key) => key + '=' + params.get(key)).join('&');
      }),
      distinctUntilChanged(),
      shareReplay()
    );
  }

  public static createValidUrlParameter(paramsAsArray: Array<string | number>): string | null {
    if (paramsAsArray === null || paramsAsArray === undefined) {
      return null;
    }
    return paramsAsArray.length !== 0 ? paramsAsArray.join(UrlService.VALUE_SEPARATOR) : null;
  }

  public async updateView(params: Params): Promise<void> {
    await this.updateUrl(params);
  }

  public async updateUrlQueryParams(params: Params): Promise<boolean> {
    return await this.updateUrl(params);
  }

  // Simultaneous requests to navigate via the router cause the earlier request to be cancelled
  // in favour of the more recent -> Requests werden in Queue gepackt und nacheinander aufgerufen
  // https://nickkell.medium.com/sequential-router-navigation-in-angular-96d6a17049f7
  public async updateUrl(params: Params | null, path?: string | null): Promise<boolean> {
    const enqueue = async () => {
      await this.queue;
      const extras: NavigationExtras = {
        queryParamsHandling: 'merge'
      };
      if (params) {
        extras.queryParams = params;
      }
      return await this.router.navigate(path ? [path] : [], extras);
    };
    this.queue = enqueue();
    return await this.queue;
  }

  public async updateUrlQueryParamsWithoutHistoryPush(params: Params): Promise<void> {
    await this.router.navigate([], {
      queryParams: params,
      replaceUrl: true,
      queryParamsHandling: 'merge'
    });
  }
}
